import { FC, Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";

import { Skeleton } from "@/components/ui/skeleton";

import { useContractAppendicesService } from "../services/use-contract-appendices-service";

import AppendixRenderError from "./appendix-render-error";

/**
 * pdf.js is a heavy dependency and most contracts have no appendices, so the
 * renderer is only fetched once we know there is something to render.
 */
const AppendixDocument = lazy(() => import("./appendix-document"));

interface ContractAppendicesProps {
  contractId?: string | number;
}

/**
 * The contract-annex PDFs that apply to this contract, rendered as the pages
 * that follow the contract itself - no section title, no per-file header, so
 * the preview reads as one document. They are part of the contract PDF the
 * download button hands over, which is why they carry no separate download of
 * their own here.
 */
export const ContractAppendices: FC<ContractAppendicesProps> = ({
  contractId,
}) => {
  const { t } = useTranslation(["appendices"]);

  const {
    data: appendices,
    isLoading,
    isError,
  } = useContractAppendicesService(contractId);

  if (isLoading) {
    return <Skeleton className="h-[600px] w-full" />;
  }

  if (isError) {
    return (
      <p className="text-sm text-destructive px-7 py-4">
        {t(
          "appendices:load-error",
          "We could not load the appendices. Please refresh the page."
        )}
      </p>
    );
  }

  if (!appendices?.length) {
    return null;
  }

  return (
    <div className="w-full">
      <Suspense fallback={<Skeleton className="h-[600px] w-full" />}>
        {appendices.map((appendix) => (
          /*
           * A boundary per appendix, not one around the list. pdf.js calls
           * `getDocument()` straight out of an effect with no try/catch of its
           * own, so anything it throws - a runtime API the browser lacks, a
           * canvas that will not allocate - unwinds all the way to the nearest
           * boundary. Without one it takes the whole signing page down; scoped
           * here it costs the reader one inline preview, and its siblings still
           * render.
           */
          <Sentry.ErrorBoundary
            key={appendix.id}
            beforeCapture={(scope) => {
              scope.setTag("feature", "appendices");
              scope.setTag("appendix.id", appendix.id);
            }}
            fallback={({ resetError }) => (
              <AppendixRenderError onRetry={resetError} />
            )}
          >
            <AppendixDocument appendix={appendix} contractId={contractId} />
          </Sentry.ErrorBoundary>
        ))}
      </Suspense>
    </div>
  );
};

export default ContractAppendices;
